import { create } from 'zustand';

interface ScreenSelectionState {
  /** All selected IDs (both devices and folders) */
  selectedIds: Set<string>;
  /** Subset of selectedIds that are folder/group IDs */
  selectedGroupIds: Set<string>;
  toggleSelected: (id: string, isGroup?: boolean) => void;
  selectAll: (ids: string[], groupIds?: string[]) => void;
  /** Drop specific ids from the selection (e.g. rows that left the Favorites view). */
  deselect: (ids: string[]) => void;
  clearSelection: () => void;
  isSelected: (id: string) => boolean;
  selectedCount: () => number;
  /** Returns only device IDs (selectedIds minus selectedGroupIds) */
  getDeviceIds: () => string[];
  /** Returns only group/folder IDs */
  getGroupIds: () => string[];
}

export const useScreenSelectionStore = create<ScreenSelectionState>((set, get) => ({
  selectedIds: new Set(),
  selectedGroupIds: new Set(),
  toggleSelected: (id, isGroup = false) =>
    set((state) => {
      const nextIds = new Set(state.selectedIds);
      const nextGroupIds = new Set(state.selectedGroupIds);
      if (nextIds.has(id)) {
        nextIds.delete(id);
        nextGroupIds.delete(id);
      } else {
        nextIds.add(id);
        if (isGroup) {
          nextGroupIds.add(id);
        }
      }
      return { selectedIds: nextIds, selectedGroupIds: nextGroupIds };
    }),
  selectAll: (ids, groupIds = []) =>
    set({ selectedIds: new Set(ids), selectedGroupIds: new Set(groupIds) }),
  deselect: (ids) =>
    set((state) => {
      if (ids.length === 0) return state;
      const nextIds = new Set(state.selectedIds);
      const nextGroupIds = new Set(state.selectedGroupIds);
      let changed = false;
      for (const id of ids) {
        if (nextIds.delete(id)) changed = true;
        nextGroupIds.delete(id);
      }
      return changed ? { selectedIds: nextIds, selectedGroupIds: nextGroupIds } : state;
    }),
  clearSelection: () => set({ selectedIds: new Set(), selectedGroupIds: new Set() }),
  isSelected: (id) => get().selectedIds.has(id),
  selectedCount: () => get().selectedIds.size,
  getDeviceIds: () => {
    const { selectedIds, selectedGroupIds } = get();
    return Array.from(selectedIds).filter((id) => !selectedGroupIds.has(id));
  },
  getGroupIds: () => Array.from(get().selectedGroupIds),
}));
